import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { CITIES, COMPANY } from "../lib/site";

const LINKS = [
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Projects", to: "/projects" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [citiesOpen, setCitiesOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-forest/95 backdrop-blur-xl border-b border-white/10" : "bg-forest/90 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 md:px-12">
        <Link to="/" data-testid="logo-link"><Logo dark /></Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors hover:text-white ${pathname === l.to ? "text-white" : "text-cream/75"}`}>{l.label}</Link>
          ))}
          <div className="relative" onMouseEnter={() => setCitiesOpen(true)} onMouseLeave={() => setCitiesOpen(false)}>
            <button data-testid="nav-cities" className="flex items-center gap-1 text-sm font-medium text-cream/75 transition-colors hover:text-white">
              Cities <ChevronDown className="h-4 w-4" />
            </button>
            <AnimatePresence>
              {citiesOpen && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                  className="absolute left-1/2 top-full grid w-64 -translate-x-1/2 grid-cols-2 gap-1 rounded-2xl border border-line bg-cream/95 p-3 shadow-xl backdrop-blur-xl">
                  {CITIES.map((c) => (
                    <Link key={c.slug} to={`/solar/${c.slug}`} data-testid={`nav-city-${c.slug}`}
                      className="rounded-lg px-3 py-2 text-sm text-ink/80 transition-colors hover:bg-sand hover:text-forest">{c.name}</Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>

        <div className="flex items-center gap-3">
          <a href={COMPANY.phoneHref} data-testid="nav-phone" className="hidden items-center gap-2 text-sm font-semibold text-white transition-colors hover:text-gold md:flex">
            <Phone className="h-4 w-4" /> {COMPANY.phone}
          </a>
          <Link to="/contact" data-testid="nav-cta"
            className="hidden rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-obsidian transition-transform duration-300 hover:scale-95 hover:bg-gold-hover md:inline-block">
            Get Free Quote
          </Link>
          <button data-testid="mobile-menu-btn" className="text-cream lg:hidden" onClick={() => setOpen(!open)}>
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-white/10 bg-forest/98 backdrop-blur-xl lg:hidden">
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <Link key={l.label} to={l.to} className="py-2 text-cream/80">{l.label}</Link>
              ))}
              <p className="pt-3 text-xs uppercase tracking-widest text-gold">Cities</p>
              <div className="grid grid-cols-2 gap-1">
                {CITIES.map((c) => (
                  <Link key={c.slug} to={`/solar/${c.slug}`} className="py-1.5 text-sm text-cream/70">{c.name}</Link>
                ))}
              </div>
              <a href={COMPANY.phoneHref} className="mt-3 flex items-center gap-2 py-2 text-sm font-semibold text-white"><Phone className="h-4 w-4" /> {COMPANY.phone}</a>
              <Link to="/contact" className="mt-2 rounded-full bg-gold px-5 py-3 text-center font-semibold text-obsidian">Get Free Quote</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
